import { VoiceEngineV2 } from '../VoiceEngineV2';
import { NativeSpeechAdapter } from '../adapters/NativeSpeechAdapter';
import { getNativeVoiceCapabilities, getNativeVoicePlugin, isNativeVoicePluginAvailable } from '../NativeVoicePlugin';
import { recordVoiceDiagnostic } from '../voiceDiagnostics';
import { addNativeAppStateListener } from '../../platform/NativeBridge';

const listeningStates = new Set(['STARTING', 'LISTENING', 'PAUSED', 'RESTARTING', 'FINALIZING']);

/**
 * Android implementation. It inherits the mature turn buffer and conservative
 * endpointing from VoiceEngineV2, replacing only the browser STT adapter.
 */
export class NativeVoiceProvider extends VoiceEngineV2 {
  constructor(options = {}) {
    super({
      ...options,
      isMobile: true,
      voiceProvider: 'android_speech_recognizer',
      speechAdapterFactory: NativeSpeechAdapter,
      speechRecognitionSupported: () => isNativeVoicePluginAvailable(),
    });
    this._nativeCapabilities = {
      native: true,
      microphone: true,
      stt: isNativeVoicePluginAvailable(),
      continuous: isNativeVoicePluginAvailable(),
      bargeIn: true,
      tts: isNativeVoicePluginAvailable(),
      permission: 'unknown',
      provider: 'android_speech_recognizer',
    };
    this._resumeContinuous = false;
    this._permissionRequestInFlight = false;
    this._permissionDialogBackgrounded = false;
    this._removeLifecycleListener = null;
    this.onPermissionStateChange = (detail) => this._handlePermissionState(detail);
    // Entre um fim natural e o rearm silencioso de 320 ms não existe adapter
    // ativo. Ainda assim, o segundo toque é uma confirmação de envio e deve
    // receber o mesmo som de término sem reabrir o reconhecimento.
    this.onManualFinishWithoutAdapter = (manualTurnId) => {
      void getNativeVoicePlugin()?.playManualCue?.({ cue: 'finish', manualTurnId });
    };
    this._nativeSetup = this._initializeNative();
    recordVoiceDiagnostic('provider_selected', { provider: 'android_speech_recognizer', platform: 'android' });
  }

  async _initializeNative() {
    await this._refreshNativeCapabilities('initial');
    const removeLifecycleListener = await addNativeAppStateListener((state) => this._handleAppState(state));
    // React StrictMode/unmount pode destruir o engine enquanto a consulta de
    // capacidades ainda está pendente. Não deixe um listener nativo órfão.
    if (this._destroyed) {
      try { removeLifecycleListener?.(); } catch { /* noop */ }
      return;
    }
    this._removeLifecycleListener = removeLifecycleListener;
  }

  async _refreshNativeCapabilities(source = 'refresh') {
    this._nativeCapabilities = { ...this._nativeCapabilities, ...(await getNativeVoiceCapabilities()) };
    if (this._destroyed) return;
    recordVoiceDiagnostic('nativeCapabilities', {
      platform: 'android',
      source,
      provider: this._nativeCapabilities.provider,
      permission: this._nativeCapabilities.permission,
      device: this._nativeCapabilities.device,
      androidVersion: this._nativeCapabilities.androidVersion,
      native: true,
      microphone: this._nativeCapabilities.microphone === true,
      stt: this._nativeCapabilities.stt === true,
      continuous: this._nativeCapabilities.continuous === true,
      bargeIn: this._nativeCapabilities.bargeIn === true,
      tts: this._nativeCapabilities.tts === true,
    });
    this._debug('native_capabilities', this._nativeCapabilities);
  }

  _handlePermissionState(detail) {
    const state = detail?.state;
    if (state === 'requesting') {
      this._permissionRequestInFlight = true;
      recordVoiceDiagnostic('microphone_permission_request', { provider: 'android_speech_recognizer' });
      return;
    }
    if (!['granted', 'denied', 'error'].includes(state)) return;
    this._permissionRequestInFlight = false;
    recordVoiceDiagnostic('microphone_permission_result', {
      provider: 'android_speech_recognizer',
      state,
      permission: detail?.permission,
      errorCode: detail?.error,
    });
    void this._refreshNativeCapabilities(`permission_${state}`);
  }

  _handleAppState(state) {
    if (this._destroyed) return;
    if (state?.isActive === false) {
      // Android can report the app inactive while its runtime permission sheet
      // is visible. Stopping the engine here used to remove the adapter before
      // the permission callback could start SpeechRecognizer.
      if (this._permissionRequestInFlight) {
        this._permissionDialogBackgrounded = true;
        recordVoiceDiagnostic('lifecycle_permission_prompt', { provider: 'android_speech_recognizer' });
        return;
      }
      // Nunca transforme uma gravação manual em contínua quando o app volta
      // do background. Isso podia recriar a escuta após o usuário já ter
      // escolhido manual/enviar, especialmente depois de um permission sheet.
      this._resumeContinuous = !this._manualMode
        && this.continuousMode
        && listeningStates.has(this._state);
      recordVoiceDiagnostic('lifecycle_background', { provider: 'android_speech_recognizer' });
      void getNativeVoicePlugin()?.stopPlayback?.({ reason: 'background' });
      this.setIdle();
      return;
    }
    if (state?.isActive === true && this._permissionDialogBackgrounded) {
      this._permissionDialogBackgrounded = false;
      recordVoiceDiagnostic('lifecycle_permission_return', { provider: 'android_speech_recognizer' });
      void this._refreshNativeCapabilities('permission_return');
      return;
    }
    if (state?.isActive === true && this._resumeContinuous) {
      this._resumeContinuous = false;
      recordVoiceDiagnostic('lifecycle_foreground', { provider: 'android_speech_recognizer' });
      window.setTimeout(() => {
        if (!this._destroyed && !this._manualMode && this.continuousMode && this._state === 'IDLE') this.startActive();
      }, 180);
    }
  }

  get capabilities() {
    return { ...this._nativeCapabilities };
  }

  get diagnostics() {
    return {
      ...super.diagnostics,
      engine: 'athena-voice-native',
      provider: this._nativeCapabilities.provider || 'android_speech_recognizer',
      capabilities: this.capabilities,
    };
  }

  getCapabilities() { return this.capabilities; }
  startListening({ mode = 'manual' } = {}) { return mode === 'continuous' ? this.startActive() : this.startManual(); }
  stopListening() { return this._manualMode ? this.finishManual() : this.finishActive(); }
  cancelListening() { return this.setIdle(); }

  stopSpeaking() {
    void getNativeVoicePlugin()?.stopPlayback?.({ reason: 'speech_interrupted' });
    return this.cancelSpeaking('speech_interrupted');
  }

  beginBargeIn() {
    // Stop native playback first. The speech adapter will open only after the
    // player has been asked to release audio focus.
    void getNativeVoicePlugin()?.stopPlayback?.({ reason: 'barge_in' });
    recordVoiceDiagnostic('bargeIn', { provider: 'android_speech_recognizer' });
    return super.beginBargeIn();
  }

  bargeIn() { return this.beginBargeIn(); }

  destroy() {
    this._resumeContinuous = false;
    this._permissionRequestInFlight = false;
    this._permissionDialogBackgrounded = false;
    try { this._removeLifecycleListener?.(); } catch { /* noop */ }
    this._removeLifecycleListener = null;
    void getNativeVoicePlugin()?.cancelListening?.({});
    void getNativeVoicePlugin()?.stopPlayback?.({ reason: 'destroyed' });
    super.destroy();
  }
}

export default NativeVoiceProvider;
